import { useMemo, useState } from 'react'
import { Star } from 'lucide-react'
import { Card, IconButton } from '../ui'
import { useCommitments, useFeed } from '../lib/queries'
import { useMarkWent } from '../lib/mutations'
import { useMarkSeen, useSeen } from '../lib/seen'
import { signalFeatures } from '../lib/entries'
import { rowTitle } from '../lib/rowMeta'
import { iso } from '../lib/calendar'
import type { Commitment, FeedRow } from '../lib/types'

/** At most this many at once — a queue that ran on would be a second Went
 *  tab rather than the "a few things to catch up on" prompt Doug asked for. */
const MAX = 5
const STARS = [1, 2, 3, 4, 5] as const

/** One identity for "nothing rated yet", read both while `useSeen()` is still
 *  loading and — until the migration in this package ships — for as long as
 *  `seen` does not exist in Postgres at all, when the query errors on every
 *  render rather than resolving once. Either way the honest answer is the
 *  same: nothing has been marked seen, so nothing here should be held back
 *  from the queue on its account. */
const EMPTY_SEEN: ReadonlyMap<string, unknown> = new Map()

/**
 * WP 8.4: "Rate what you saw" — Doug's own words, "keep [past things] in the
 * calendar and rate it, so we can start to build an ongoing taste profile."
 * Past commitments with an `entry_id` and no `seen` row yet, newest first, at
 * most five.
 *
 * Self-contained (its own three queries, no props) so it can be mounted
 * anywhere. WP 8.5 mounts it at the top of the Year.
 *
 * **WP 8.5's boundary**: the Year is now forward-looking, `on >= today`
 * (`Year.tsx`), and this queue is `on_date < today` — strictly the
 * complement, not `<=`. A commitment dated today sits in the window both
 * screens could plausibly claim, and CLAUDE.md's "one value, two jobs" bug
 * is exactly what drawing it in both would be: the Year already gives
 * today's commitment its own card, which opens the full `EventSheet` where
 * "Seen it · rate" (`EventActions`) reaches the same five stars this queue
 * offers, with the title, venue and date attached rather than a bare row.
 * So the Year owns today outright, and this queue's job starts the day
 * after: a commitment that has fully left the Year's own window is the one
 * with no card anywhere left to rate it from. `alreadyHappened`
 * (`entries.ts`) reads the same "no showing left ahead of it" fact but for a
 * showing, not a calendar day, which is why it is not reused here.
 *
 * (8.4 shipped `<=` on purpose, over the brief's own `<`, because its one
 * live worked example — Cassirer, 27 Sept — was dated the render's own
 * "today" and needed showing. Widening to `<=` was right for a queue that,
 * at the time, had nothing else on the Year to defer to; now that the Year
 * itself reaches back to today, the reason for the widening is gone.)
 *
 * A star tap rates and removes the row in one motion: `useMarkSeen` writes
 * the `seen` row, `useSeen`'s own cache is invalidated, and the next render
 * no longer finds this commitment queue-worthy. "Didn't go" has no such row
 * to key off of — `markWent(false)` only ever wrote `taste_signal`, an
 * append-only log with nothing to read an "already answered" flag off — so
 * it is tracked locally for the rest of this visit, the same trade-off
 * `Shortlist.tsx`'s own Went-tab checkbox already makes and for the same
 * reason (see its own `answered` comment).
 */
export function RateQueue() {
  const commitmentsQ = useCommitments()
  const feedQ = useFeed()
  const seenQ = useSeen()
  const markM = useMarkSeen()
  const wentM = useMarkWent()
  const [answered, setAnswered] = useState<ReadonlySet<string>>(() => new Set())

  const today = iso(new Date())
  const feedById = useMemo(() => new Map((feedQ.data ?? []).map(e => [e.id, e])), [feedQ.data])
  const seen = seenQ.data ?? EMPTY_SEEN

  const queue = useMemo(() => {
    // `<`, not `<=` (WP 8.5, reversing 8.4's own widening — see this file's
    // header): a commitment dated today already has a card of its own on
    // the Year, which is where it gets rated from now.
    return (commitmentsQ.data ?? [])
      .filter((c): c is Commitment & { entry_id: string } =>
        !!c.entry_id && c.on_date < today && !seen.has(c.entry_id) && !answered.has(c.id))
      .sort((a, b) => (a.on_date < b.on_date ? 1 : -1))
      .slice(0, MAX)
  }, [commitmentsQ.data, seen, today, answered])

  if (!queue.length) return null

  const featuresOf = (entry: FeedRow | null) => (entry ? signalFeatures(entry) : {})

  const rate = (c: Commitment & { entry_id: string }, entry: FeedRow | null, stars: number) => {
    markM.mutate({ entryId: c.entry_id, stars, features: featuresOf(entry) })
  }

  const didntGo = (c: Commitment & { entry_id: string }, entry: FeedRow | null) => {
    wentM.mutate({ entryId: c.entry_id, went: false, features: featuresOf(entry) })
    setAnswered(prev => new Set(prev).add(c.id))
  }

  return (
    <Card className="ratequeue">
      <p className="ratequeue__head">Rate what you saw</p>
      {queue.map(c => {
        const entry = feedById.get(c.entry_id) ?? null
        const title = entry ? rowTitle(entry) : c.title
        return (
          <div key={c.id} className="ratequeue__row">
            <p className="ratequeue__title">{title}</p>
            <div className="ratequeue__actions">
              {STARS.map(n => (
                <IconButton
                  key={n} size="sm" label={`Rate ${title} ${n} star${n === 1 ? '' : 's'}`}
                  onClick={() => rate(c, entry, n)}
                >
                  <Star size={18} />
                </IconButton>
              ))}
              <button type="button" className="ratequeue__skip" onClick={() => didntGo(c, entry)}>
                Didn't go
              </button>
            </div>
          </div>
        )
      })}
    </Card>
  )
}
